import React from 'react';
import { Box, Typography, Button, Stack, alpha, useTheme, Tooltip } from '@mui/material';
import {
  Map as MapIcon,
  Crop as RoiIcon,
  LiveTv as LiveIcon,
  Storage as StorageIcon,
  Folder as FolderIcon,
  PlayCircleOutline as SimulatorIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { hasRoleAtLeast, UserRole } from '../../utils/permissions';
import usePermissions from '../../hooks/usePermissions';

interface Tool {
  id: string;
  label: string;
  icon: React.ReactNode;
  path: string;
  color: string;
  disabled?: boolean;
  /** 미달이면 도구 카드 자체를 숨긴다 (LayoutView 메뉴 + ProtectedRoute 와 sync). */
  requireRole?: UserRole;
}

interface ToolsBarProps {
  projectId: string;
}

const ToolsBar: React.FC<ToolsBarProps> = ({ projectId }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { role } = usePermissions();

  const allTools: Tool[] = [
    { id: 'map-editor', label: '맵 에디터', icon: <MapIcon />, path: `/project/${projectId}/map-editor`, color: '#1a73e8', requireRole: 'editor' },
    { id: 'roi-editor', label: 'ROI 편집', icon: <RoiIcon />, path: `/project/${projectId}/roi-editor`, color: '#e3742f', requireRole: 'editor' },
    { id: 'live-monitor', label: '실시간 관제', icon: <LiveIcon />, path: `/project/${projectId}/live-status`, color: '#d93025' },
    { id: 'file-repo', label: '파일 보관함', icon: <FolderIcon />, path: `/project/${projectId}/file-repository`, color: '#5f6368', requireRole: 'admin' },
    { id: 'learning-data', label: '학습 데이터', icon: <StorageIcon />, path: `/project/${projectId}/learning-data-management`, color: '#188038', disabled: true, requireRole: 'editor' },
    { id: 'simulator', label: '시뮬레이터', icon: <SimulatorIcon />, path: `/project/${projectId}/simulator`, color: '#9334e6', requireRole: 'editor' },
  ];

  const tools = allTools.filter(
    (t) => !t.requireRole || hasRoleAtLeast(role, t.requireRole),
  );

  return (
    // 카드(Paper)는 개요 그리드 셀(OverviewSection)이 이미 감싼다 — 여기서 또
    // 두르면 카드 속 카드가 되어 패딩이 겹치고 셀 높이를 넘겨 내용이 잘린다.
    <Box data-testid="tools-bar">
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        작업 도구
      </Typography>
      {/* 가로 스크롤 대신 줄바꿈 — 개요 카드 안에 스크롤바를 두지 않는다. */}
      <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap">
        {tools.map((t) => {
          const button = (
            <Button
              key={t.id}
              variant="outlined"
              disabled={t.disabled}
              onClick={() => navigate(t.path)}
              startIcon={
                <Box
                  sx={{
                    color: t.color,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {t.icon}
                </Box>
              }
              data-testid={`tool-btn-${t.id}`}
              sx={{
                flexShrink: 0,
                minWidth: 140,
                justifyContent: 'flex-start',
                borderColor: alpha(t.color, 0.4),
                color: 'text.primary',
                '&:hover': {
                  borderColor: t.color,
                  bgcolor: alpha(t.color, theme.palette.mode === 'dark' ? 0.15 : 0.08),
                },
              }}
            >
              {t.label}
            </Button>
          );
          return t.disabled ? (
            <Tooltip key={t.id} title="준비 중">
              <span>{button}</span>
            </Tooltip>
          ) : (
            button
          );
        })}
      </Stack>
    </Box>
  );
};

export default ToolsBar;
